import type {
  AccountType,
  ConsentPurpose,
  ConsentStatus,
  DirectoryPharmacy,
  EnvelopeKind,
  HiType,
  HolderMemberView,
  MyInvitation,
  MyPatientLink,
  MyPractice,
  PharmacyLink,
  PracticeInvitation,
  PracticeMember,
  PracticePatientLink,
  PracticeRegistration,
  PracticeType,
  PublicAccount,
  PublicConsent,
  PublicHub,
  PublicPractice,
  RecordHolder,
  Role,
  SignupAccountType,
} from '@curentia/shared';

/** An API refusal with the server's stable code. Status 0 means the server could not be reached. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get offline(): boolean {
    return this.status === 0;
  }
}

export interface AuthResult {
  account: PublicAccount;
  accessToken: string;
  expiresAt: string;
}

export interface RegisterBody {
  type: SignupAccountType;
  name: string;
  mobile: string;
  email?: string;
  abhaNumber?: string;
  abhaAddress?: string;
  registration?: { council: string; number: string };
  loginKey: string;
}

export interface DeviceRecord {
  id: string;
  label: string;
  encryptionKey: string;
  signingKey: string;
  createdAt: string;
  revokedAt: string | null;
}

/** A device's public keys from the key directory. */
export interface DirectoryDevice {
  id: string;
  encryptionKey: string;
  signingKey: string;
  createdAt: string;
  revokedAt: string | null;
}

export interface InboxEnvelope {
  id: string;
  kind: string;
  header: string;
  ciphertext: string;
  signature: string;
  senderAccountId: string;
  senderDevice: string;
  receivedAt: string;
  expiresAt: string;
}

export interface ConsentRequest {
  patientId: string;
  practiceId: string;
  hiTypes: HiType[];
  from: string;
  to: string;
  expiresAt: string;
  purpose?: ConsentPurpose;
}

export interface NewPractice {
  name: string;
  type: PracticeType;
  city: string;
  registration?: PracticeRegistration;
  recordHolder?: RecordHolder;
}

const SESSION_ERRORS = new Set(['unauthenticated', 'invalid_token', 'session_ended']);

/**
 * The Curentia API. Tokens live only in memory. When a signed-in call fails
 * because the token expired, the client asks the app to sign in again once,
 * with the login key it keeps in memory while unlocked, and retries.
 */
export class Api {
  private token: string | null = null;
  private reauth: (() => Promise<boolean>) | null = null;

  constructor(readonly baseUrl: string) {}

  setToken(token: string | null): void {
    this.token = token;
  }

  get signedIn(): boolean {
    return this.token !== null;
  }

  /** The current access token, for the live alert handshake. */
  get currentToken(): string | null {
    return this.token;
  }

  /** How to sign in again when a token expires. Pass null to stop. */
  onReauth(handler: (() => Promise<boolean>) | null): void {
    this.reauth = handler;
  }

  async call<T>(method: 'GET' | 'POST', path: string, body?: unknown, auth = true, retry = true): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api${path}`, {
        method,
        headers: {
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(auth && this.token ? { Authorization: `Bearer ${this.token}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiError(0, 'offline', 'Curentia cannot reach its server. Check your connection.');
    }
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (response.ok) return data as T;
    const code = typeof data.error === 'string' ? data.error : 'error';
    if (auth && retry && response.status === 401 && SESSION_ERRORS.has(code) && this.reauth && (await this.reauth())) {
      return this.call<T>(method, path, body, auth, false);
    }
    const message = typeof data.message === 'string' ? data.message : 'Something went wrong. Try again.';
    const { error: _error, message: _message, ...extra } = data;
    throw new ApiError(response.status, code, message, extra);
  }

  prelogin(type: AccountType, mobile: string) {
    return this.call<{ salt: string; kdf: string }>('POST', '/auth/prelogin', { type, mobile }, false);
  }

  register(body: RegisterBody) {
    return this.call<AuthResult>('POST', '/auth/register', body, false);
  }

  login(type: AccountType, mobile: string, loginKey: string) {
    return this.call<AuthResult>('POST', '/auth/login', { type, mobile, loginKey }, false);
  }

  resetLoginKey(body: { type: AccountType; mobile: string; deviceId: string; newLoginKey: string; signedAt: string; signature: string }) {
    return this.call<AuthResult>('POST', '/auth/reset-login-key', body, false);
  }

  me() {
    return this.call<{ account: PublicAccount }>('GET', '/auth/me');
  }

  /** A doctor's or pharmacist's qualifications, as their prescriptions print them. An empty text removes them. */
  setQualifications(qualifications: string) {
    return this.call<{ account: PublicAccount }>('POST', '/auth/me/qualifications', { qualifications });
  }

  registerDevice(body: { label: string; encryptionKey: string; signingKey: string; proof: string }) {
    return this.call<{ device: DeviceRecord }>('POST', '/devices', body);
  }

  devices() {
    return this.call<{ devices: DeviceRecord[] }>('GET', '/devices');
  }

  /** Envelope kinds are not personal or clinical data, so they may go in the query string. */
  inbox(deviceId: string, limit = 50, kinds?: readonly EnvelopeKind[]) {
    const query = new URLSearchParams({ limit: String(limit), ...(kinds ? { kinds: kinds.join(',') } : {}) });
    return this.call<{ envelopes: InboxEnvelope[]; more: boolean }>('GET', `/devices/${deviceId}/envelopes?${query.toString()}`);
  }

  // ---- Practices -------------------------------------------------------------

  practices() {
    return this.call<{ practices: MyPractice[] }>('GET', '/practices');
  }

  practice(id: string) {
    return this.call<MyPractice>('GET', `/practices/${id}`);
  }

  createPractice(body: NewPractice) {
    return this.call<MyPractice>('POST', '/practices', body);
  }

  /** The address and phone number the practice's prescriptions print. Empty texts remove them. */
  setLetterhead(practiceId: string, letterhead: { address: string; phone: string }) {
    return this.call<{ practice: PublicPractice }>('POST', `/practices/${practiceId}/letterhead`, letterhead);
  }

  setRecordHolder(practiceId: string, deviceId: string) {
    return this.call<{ practice: PublicPractice }>('POST', `/practices/${practiceId}/record-holder`, { deviceId });
  }

  members(practiceId: string) {
    return this.call<{ members: PracticeMember[] }>('GET', `/practices/${practiceId}/members`);
  }

  changeRoles(practiceId: string, accountId: string, roles: Role[]) {
    return this.call<{ member: PracticeMember }>('POST', `/practices/${practiceId}/members/${accountId}/roles`, { roles });
  }

  removeMember(practiceId: string, accountId: string) {
    return this.call<{ removed: true }>('POST', `/practices/${practiceId}/members/${accountId}/remove`, {});
  }

  invite(practiceId: string, roles: Role[], label?: string) {
    return this.call<{ invitation: PracticeInvitation; code: string }>('POST', `/practices/${practiceId}/invitations`, {
      roles,
      ...(label ? { label } : {}),
    });
  }

  invitations(practiceId: string) {
    return this.call<{ invitations: PracticeInvitation[] }>('GET', `/practices/${practiceId}/invitations`);
  }

  approveInvitation(practiceId: string, invitationId: string, roles?: Role[]) {
    return this.call<{ invitation: PracticeInvitation; member: PracticeMember }>(
      'POST',
      `/practices/${practiceId}/invitations/${invitationId}/approve`,
      roles ? { roles } : {},
    );
  }

  cancelInvitation(practiceId: string, invitationId: string) {
    return this.call<{ invitation: PracticeInvitation }>('POST', `/practices/${practiceId}/invitations/${invitationId}/cancel`, {});
  }

  acceptInvitation(code: string) {
    return this.call<{ invitation: MyInvitation }>('POST', '/invitations/accept', { code });
  }

  myInvitations() {
    return this.call<{ invitations: MyInvitation[] }>('GET', '/invitations');
  }

  // ---- Hubs, keys and the relay -------------------------------------------------

  createHubPairing(practiceId: string) {
    return this.call<{ code: string; expiresAt: string }>('POST', `/practices/${practiceId}/hub-pairing`, {});
  }

  practiceHub(practiceId: string) {
    return this.call<{ hub: PublicHub | null }>('GET', `/practices/${practiceId}/hub`);
  }

  unpairHub(practiceId: string) {
    return this.call<{ practice: PublicPractice }>('POST', `/practices/${practiceId}/hub/unpair`, {});
  }

  /** The practice's record holder devices, for sealing requests to them. */
  practiceKeys(practiceId: string) {
    return this.call<{ practiceId: string; devices: DirectoryDevice[] }>('GET', `/keys/practices/${practiceId}`);
  }

  deviceKeys(deviceId: string) {
    return this.call<{ device: DirectoryDevice & { accountId: string; accountType: AccountType | null } }>('GET', `/keys/devices/${deviceId}`);
  }

  accountKeys(accountId: string) {
    return this.call<{ accountId: string; accountType: AccountType; devices: DirectoryDevice[] }>('GET', `/keys/accounts/${accountId}`);
  }

  sendEnvelopes(envelopes: { header: string; ciphertext: string; signature: string }[]) {
    return this.call<{ accepted: { id: string; expiresAt: string }[] }>('POST', '/envelopes', { envelopes });
  }

  ackEnvelopes(deviceId: string, ids: string[]) {
    return this.call<{ deleted: number }>('POST', `/devices/${deviceId}/envelopes/ack`, { ids });
  }

  /** For a device that holds a practice's records: each member's roles and device keys. */
  holderMembers(practiceId: string) {
    return this.call<{ members: HolderMemberView[] }>('GET', `/holders/${practiceId}/members`);
  }

  // ---- Patient links -------------------------------------------------------------

  /** The patient's one-time code to show in the clinic. */
  createLinkCode() {
    return this.call<{ code: string; expiresAt: string }>('POST', '/patient-links/code', {});
  }

  linkWithCode(practiceId: string, code: string) {
    return this.call<{ link: PracticePatientLink }>('POST', '/patient-links', { practiceId, code });
  }

  /** The mobile number or ABHA goes in the body, never the URL. */
  requestLink(practiceId: string, identifier: string) {
    return this.call<{ sent: true }>('POST', '/patient-links/requests', { practiceId, identifier });
  }

  myLinks() {
    return this.call<{ links: MyPatientLink[]; next: string | null }>('GET', '/patient-links');
  }

  practiceLinks(practiceId: string) {
    return this.call<{ links: PracticePatientLink[]; next: string | null }>('GET', `/patient-links?practiceId=${practiceId}&limit=200`);
  }

  approveLink(linkId: string) {
    return this.call<{ link: MyPatientLink }>('POST', `/patient-links/${linkId}/approve`, {});
  }

  declineLink(linkId: string) {
    return this.call<{ link: MyPatientLink }>('POST', `/patient-links/${linkId}/decline`, {});
  }

  removeLink(linkId: string) {
    return this.call<{ removed: true }>('POST', `/patient-links/${linkId}/remove`, {});
  }

  // ---- Consents ----------------------------------------------------------------

  requestConsent(body: ConsentRequest) {
    return this.call<{ consent: PublicConsent }>('POST', '/consents', body);
  }

  /** A patient lists their own; a doctor or a record holder names the practice. IDs only in the query string. */
  consents(query: { practiceId?: string; patientId?: string; status?: ConsentStatus; before?: string } = {}) {
    const params = new URLSearchParams({ limit: '100', ...Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined)) });
    return this.call<{ consents: PublicConsent[]; next: string | null }>('GET', `/consents?${params.toString()}`);
  }

  consent(consentId: string) {
    return this.call<{ consent: PublicConsent }>('GET', `/consents/${consentId}`);
  }

  approveConsent(consentId: string, body: { deviceId: string; sources: string[]; signedAt: string; signature: string }) {
    return this.call<{ consent: PublicConsent }>('POST', `/consents/${consentId}/approve`, body);
  }

  denyConsent(consentId: string) {
    return this.call<{ consent: PublicConsent }>('POST', `/consents/${consentId}/deny`, {});
  }

  revokeConsent(consentId: string) {
    return this.call<{ consent: PublicConsent }>('POST', `/consents/${consentId}/revoke`, {});
  }

  // ---- Pharmacy links ----------------------------------------------------------

  /** A practice's public name, type and city, for example to show which pharmacy sent a notice. */
  directoryPractice(practiceId: string) {
    return this.call<{ practice: { id: string; name: string; type: PracticeType; city: string } }>('GET', `/directory/practices/${practiceId}`);
  }

  /** City and name are business details, not personal or clinical data, so they may go in the query string. */
  searchPharmacies(city: string, name?: string) {
    const query = new URLSearchParams({ city, ...(name ? { name } : {}) });
    return this.call<{ pharmacies: DirectoryPharmacy[] }>('GET', `/directory/pharmacies?${query.toString()}`);
  }

  pharmacyLinks() {
    return this.call<{ links: PharmacyLink[] }>('GET', '/pharmacy-links');
  }

  requestPharmacyLink(pharmacyId: string) {
    return this.call<{ link: PharmacyLink }>('POST', '/pharmacy-links', { pharmacyId });
  }

  linksToPharmacy(practiceId: string) {
    return this.call<{ links: PharmacyLink[] }>('GET', `/practices/${practiceId}/pharmacy-links`);
  }

  acceptPharmacyLink(linkId: string) {
    return this.call<{ link: PharmacyLink }>('POST', `/pharmacy-links/${linkId}/accept`, {});
  }

  removePharmacyLink(linkId: string) {
    return this.call<{ removed: true }>('POST', `/pharmacy-links/${linkId}/remove`, {});
  }
}
